import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchCutList,
  updateCutListStatus,
  updateCutListMachine,
  updateItemStatus,
  moveItemsToMachine,
  reorderItems
} from '../store/slices/cutlistsSlice';
import api from '../services/api';
import { fetchMachines } from '../store/slices/machinesSlice';

// Priority from which an order counts as urgent (marketplace orders get 8)
const URGENT_PRIORITY = 8;

const ItemStatusBadge = ({ status }) => {
  const colors = {
    pending: 'secondary',
    cutting: 'warning',
    completed: 'success',
    error: 'danger'
  };

  const labels = {
    pending: 'Wachtend',
    cutting: 'Snijden',
    completed: 'Voltooid',
    error: 'Fout'
  };

  return (
    <span className={`badge bg-${colors[status] || 'secondary'}`}>
      {labels[status] || status}
    </span>
  );
};

const CutListDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentCutList: cutList, loading } = useSelector((state) => state.cutlists);
  const { machines, loading: machinesLoading } = useSelector((state) => state.machines);

  // Debug log
  console.log('Machines loaded:', machines?.length, 'Loading:', machinesLoading, machines);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [movingItems, setMovingItems] = useState(false);
  const [showMoveDropdown, setShowMoveDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(null); // item._id of open dropdown
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printResult, setPrintResult] = useState(null);
  const [printingItemId, setPrintingItemId] = useState(null);

  // Product detail modal state
  const [showProductDetailModal, setShowProductDetailModal] = useState(false);
  const [productDetail, setProductDetail] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);

  const openProductDetail = useCallback(async (sku) => {
    if (!sku) return;
    setShowProductDetailModal(true);
    setProductDetail(null);
    setLoadingProduct(true);
    try {
      const response = await api.get('/products', { params: { search: sku, limit: 1 } });
      const products = response.data.products || [];
      const match = products.find(p => p.productCode === sku) || products[0];
      setProductDetail(match || null);
    } catch (err) {
      console.error('Product ophalen mislukt:', err);
    } finally {
      setLoadingProduct(false);
    }
  }, []);

  useEffect(() => {
    dispatch(fetchCutList(id));
  }, [dispatch, id]);

  // Fetch machines separately to ensure they load
  useEffect(() => {
    if (!machines || machines.length === 0) {
      dispatch(fetchMachines());
    }
  }, [dispatch, machines]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown')) {
        setShowMoveDropdown(false);
        setShowItemDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleStatusChange = (status) => {
    dispatch(updateCutListStatus({ id, status }));
  };

  const handleMachineChange = (machineId) => {
    dispatch(updateCutListMachine({ id, machineId }));
  };

  const handleItemStatusChange = (itemId, status) => {
    dispatch(updateItemStatus({ cutListId: id, itemId, status }));
    setSelectedItem(null);
  };

  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const selectAllItems = () => {
    if (selectedItems.size === cutList.items?.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(cutList.items?.map(item => item._id)));
    }
  };

  const selectOrderItems = (orderNumber) => {
    const orderItems = cutList.items?.filter(item => item.orderNumber === orderNumber) || [];
    const newSelected = new Set(selectedItems);
    const allSelected = orderItems.every(item => newSelected.has(item._id));

    if (allSelected) {
      // Deselect all items from this order
      orderItems.forEach(item => newSelected.delete(item._id));
    } else {
      // Select all items from this order
      orderItems.forEach(item => newSelected.add(item._id));
    }
    setSelectedItems(newSelected);
  };

  const handleMoveItems = async (targetMachineId) => {
    if (selectedItems.size === 0) return;

    setMovingItems(true);
    const result = await dispatch(moveItemsToMachine({
      id,
      itemIds: Array.from(selectedItems),
      targetMachineId
    }));
    setMovingItems(false);
    setSelectedItems(new Set());

    // If source was deleted, redirect to cutlists overview
    if (result.payload?.sourceDeleted) {
      navigate('/cutlists');
    }
  };

  const handleMoveItem = async (itemId, targetMachineId) => {
    setMovingItems(true);
    const result = await dispatch(moveItemsToMachine({
      id,
      itemIds: [itemId],
      targetMachineId
    }));
    setMovingItems(false);

    // If source was deleted, redirect to cutlists overview
    if (result.payload?.sourceDeleted) {
      navigate('/cutlists');
    }
  };

  const handleDownloadCsv = async () => {
    setDownloadingCsv(true);
    try {
      const response = await api.get(`/cutlists/${id}/csv`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${cutList.cutListId || id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Fout bij downloaden CSV');
    }
    setDownloadingCsv(false);
  };

  const handlePrint = async () => {
    setPrinting(true);
    setPrintResult(null);
    try {
      const response = await api.post(`/cutlists/${id}/print`);
      setPrintResult({ success: true, message: response.data.message });
    } catch (error) {
      setPrintResult({ success: false, message: error.response?.data?.error || 'Printen mislukt' });
    }
    setPrinting(false);
  };

  const handlePrintItem = async (itemId) => {
    setPrintingItemId(itemId);
    try {
      const response = await api.post(`/cutlists/${id}/items/${itemId}/print`);
      setPrintResult({ success: true, message: response.data.message });
    } catch (error) {
      setPrintResult({ success: false, message: error.response?.data?.error || 'Printen mislukt' });
    }
    setPrintingItemId(null);
  };

  // Get unique orders in this cutlist
  const uniqueOrders = cutList?.items
    ? [...new Set(cutList.items.map(item => item.orderNumber))].filter(Boolean)
    : [];

  // Check if cutlist is editable
  const isEditable = cutList?.status === 'draft' || cutList?.status === 'ready';

  // Get other machines (excluding current)
  // cutList.machine can be an object with _id or just a string ID
  const currentMachineId = cutList?.machine?._id || cutList?.machine;
  const otherMachines = machines.filter(m => {
    // Include if machine is active (or isActive is undefined - show all)
    const isActive = m.isActive !== false;
    // Exclude current machine
    const isNotCurrent = m._id !== currentMachineId;
    return isActive && isNotCurrent;
  });

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  if (!cutList) {
    return (
      <div className="alert alert-warning">
        Snijlijst niet gevonden
        <Link to="/cutlists" className="btn btn-link">Terug naar snijlijsten</Link>
      </div>
    );
  }

  const progress = cutList.stats
    ? Math.round((cutList.stats.completedItems / cutList.stats.totalItems) * 100) || 0
    : 0;

  return (
    <div className="cutlist-detail">
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><Link to="/cutlists">Snijlijsten</Link></li>
              <li className="breadcrumb-item active">{cutList.cutListId}</li>
            </ol>
          </nav>
          <h1>{cutList.name || cutList.cutListId}</h1>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-outline-secondary"
            onClick={handleDownloadCsv}
            disabled={downloadingCsv || !cutList.items?.length}
            title="Download CSV"
          >
            {downloadingCsv ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : (
              <i className="bi bi-download me-1"></i>
            )}
            CSV
          </button>
          {cutList.machine?.printNode?.apiKey && cutList.machine?.printNode?.printerId && (
            <button
              className="btn btn-outline-secondary"
              onClick={handlePrint}
              disabled={printing || !cutList.items?.length}
              title="Print labels"
            >
              {printing ? (
                <span className="spinner-border spinner-border-sm"></span>
              ) : (
                <i className="bi bi-printer me-1"></i>
              )}
              Print
            </button>
          )}
          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={cutList.status}
            onChange={async (e) => {
              const newStatus = e.target.value;
              if (newStatus === 'in_progress' && cutList.status === 'draft') {
                const readyResult = await dispatch(updateCutListStatus({ id, status: 'ready' }));
                if (readyResult.meta.requestStatus === 'rejected') {
                  alert(readyResult.payload || 'Fout bij bijwerken status');
                  return;
                }
              }
              const result = await dispatch(updateCutListStatus({ id, status: newStatus }));
              if (result.meta.requestStatus === 'rejected') {
                alert(result.payload || 'Fout bij bijwerken status');
              }
            }}
          >
            <option value="draft">Concept</option>
            <option value="in_progress">In productie</option>
            <option value="completed">Voltooid</option>
          </select>
          {(cutList.status === 'draft' || cutList.status === 'ready') && (
            <button
              className="btn btn-success"
              onClick={async () => {
                if (cutList.status === 'draft') {
                  const readyResult = await dispatch(updateCutListStatus({ id, status: 'ready' }));
                  if (readyResult.meta.requestStatus === 'rejected') {
                    alert(readyResult.payload || 'Fout bij bijwerken status');
                    return;
                  }
                }
                const result = await dispatch(updateCutListStatus({ id, status: 'in_progress' }));
                if (result.meta.requestStatus === 'rejected') {
                  alert(result.payload || 'Fout bij starten productie');
                }
              }}
            >
              <i className="bi bi-play-fill me-2"></i>
              Start productie
            </button>
          )}
          {cutList.status === 'in_progress' && (
            <button
              className="btn btn-success"
              onClick={() => handleStatusChange('completed')}
            >
              <i className="bi bi-check me-2"></i>
              Voltooid
            </button>
          )}
        </div>
      </div>

      {printResult && (
        <div className={`alert alert-${printResult.success ? 'success' : 'danger'} alert-dismissible fade show`}>
          <i className={`bi bi-${printResult.success ? 'check-circle' : 'x-circle'} me-2`}></i>
          {printResult.message}
          <button type="button" className="btn-close" onClick={() => setPrintResult(null)}></button>
        </div>
      )}

      <div className="row">
        <div className="col-lg-8">
          {/* Progress */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-medium">Voortgang</span>
                <span>{cutList.stats?.completedItems || 0} / {cutList.stats?.totalItems || 0} items</span>
              </div>
              <div className="progress" style={{ height: '20px' }}>
                <div
                  className={`progress-bar bg-${progress === 100 ? 'success' : 'primary'}`}
                  style={{ width: `${progress}%` }}
                >
                  {progress}%
                </div>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Items ({cutList.items?.length || 0})</h5>
              {isEditable && selectedItems.size > 0 && (
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary">{selectedItems.size} geselecteerd</span>
                  <div className="dropdown" style={{ position: 'relative' }}>
                    <button
                      className="btn btn-sm btn-outline-primary dropdown-toggle"
                      onClick={() => setShowMoveDropdown(!showMoveDropdown)}
                      disabled={movingItems}
                    >
                      {movingItems ? (
                        <span className="spinner-border spinner-border-sm me-1"></span>
                      ) : (
                        <i className="bi bi-arrow-right-circle me-1"></i>
                      )}
                      Verplaats naar...
                    </button>
                    {showMoveDropdown && (
                      <ul className="dropdown-menu dropdown-menu-end show" style={{ position: 'absolute', right: 0, top: '100%' }}>
                        {otherMachines.map(machine => (
                          <li key={machine._id}>
                            <button
                              className="dropdown-item"
                              onClick={() => {
                                handleMoveItems(machine._id);
                                setShowMoveDropdown(false);
                              }}
                            >
                              {machine.name}
                            </button>
                          </li>
                        ))}
                        {otherMachines.length === 0 && (
                          <li><span className="dropdown-item text-muted">Geen andere machines beschikbaar</span></li>
                        )}
                      </ul>
                    )}
                  </div>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setSelectedItems(new Set())}
                  >
                    Deselecteer
                  </button>
                </div>
              )}
            </div>

            {/* Quick order selection */}
            {isEditable && uniqueOrders.length > 1 && (
              <div className="card-body border-bottom py-2 bg-light">
                <small className="text-muted me-2">Selecteer order:</small>
                {uniqueOrders.map(orderNumber => {
                  const orderItems = cutList.items?.filter(item => item.orderNumber === orderNumber) || [];
                  const allSelected = orderItems.every(item => selectedItems.has(item._id));
                  return (
                    <button
                      key={orderNumber}
                      className={`btn btn-sm me-1 ${allSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => selectOrderItems(orderNumber)}
                    >
                      {orderNumber} ({orderItems.length})
                    </button>
                  );
                })}
              </div>
            )}

            <div className="card-body p-0">
              {cutList.items?.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        {isEditable && (
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedItems.size === cutList.items?.length && cutList.items?.length > 0}
                              onChange={selectAllItems}
                              title="Selecteer alles"
                            />
                          </th>
                        )}
                        <th style={{ width: '50px' }}>#</th>
                        <th>Order</th>
                        <th>Product</th>
                        <th>Afmetingen</th>
                        <th>Vorm</th>
                        <th>Aantal</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cutList.items.map((item, index) => (
                        <tr
                          key={item._id}
                          className={`${item.status === 'completed' ? 'table-success' : item.status === 'error' ? 'table-danger' : ''} ${selectedItems.has(item._id) ? 'table-primary' : ''}`}
                        >
                          {isEditable && (
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedItems.has(item._id)}
                                onChange={() => toggleItemSelection(item._id)}
                              />
                            </td>
                          )}
                          <td className="text-muted">{item.position || index + 1}</td>
                          <td>
                            {item.order ? (
                              <Link to={`/orders/${item.order._id || item.order}`}>
                                {item.orderNumber || item.order.orderNumber || item.order}
                              </Link>
                            ) : (
                              item.orderNumber || '-'
                            )}
                            {item.priority >= URGENT_PRIORITY && (
                              <span className="badge bg-danger ms-2" title="Spoedorder (marketplace)">Spoed</span>
                            )}
                            {item.orderStarted && item.priority < URGENT_PRIORITY && item.status === 'pending' && (
                              <span className="badge bg-warning text-dark ms-2" title="Een andere regel van deze order is al in productie: deze regel gaat voor">Order gestart</span>
                            )}
                          </td>
                          <td>
                            <div className="fw-medium">
                              <a
                                href="#"
                                className="text-decoration-none text-dark"
                                onClick={(e) => { e.preventDefault(); openProductDetail(item.productCode); }}
                                style={{ borderBottom: '1px dashed #6c757d' }}
                              >
                                {item.productName}
                              </a>
                            </div>
                            <small className="text-muted"><code>{item.productCode}</code></small>
                            {item.sourcePartCode && (
                              <div>
                                <span
                                  className="badge bg-light text-dark border mt-1"
                                  title="Dit materiaal heeft een parent in Picqer en wordt van die rol gesneden. Bij afronden wordt de voorraad in Picqer gecorrigeerd."
                                >
                                  <i className="bi bi-arrow-left-right me-1"></i>
                                  {item.sourcePartCode}, gesneden van rol {item.partCode}
                                </span>
                              </div>
                            )}
                          </td>
                          <td>
                            {(() => {
                              // Parse dimensions from notes if available (exact keys)
                              if (item.notes) {
                                const breedteMatch = item.notes.match(/Breedte\s*\(in cm\):\s*(\d+(?:[.,]\d+)?)/i);
                                const lengteMatch = item.notes.match(/^Lengte\s*\(in cm\):\s*(\d+(?:[.,]\d+)?)/im);
                                if (breedteMatch && lengteMatch) {
                                  return `${breedteMatch[1]} x ${lengteMatch[1]} cm`;
                                }
                              }
                              // Fallback to stored dimensions (in mm)
                              if (item.dimensions?.width && item.dimensions?.height) {
                                return `${item.dimensions.width} x ${item.dimensions.height} ${item.dimensions.unit || 'mm'}`;
                              }
                              return '-';
                            })()}
                          </td>
                          <td>{item.shapeLabel || item.shape || '-'}</td>
                          <td>{item.quantity}</td>
                          <td>
                            <ItemStatusBadge status={item.status} />
                          </td>
                          <td>
                            {cutList.machine?.printNode?.apiKey && cutList.machine?.printNode?.printerId && (
                              <button
                                className="btn btn-sm btn-outline-info me-1"
                                onClick={() => handlePrintItem(item._id)}
                                disabled={printingItemId === item._id}
                                title="Print label"
                              >
                                {printingItemId === item._id ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                  <i className="bi bi-printer"></i>
                                )}
                              </button>
                            )}
                            {cutList.status === 'in_progress' && item.status !== 'completed' && (
                              <div className="btn-group btn-group-sm">
                                {item.status === 'pending' && (
                                  <button
                                    className="btn btn-outline-warning"
                                    onClick={() => handleItemStatusChange(item._id, 'cutting')}
                                    title="Start snijden"
                                  >
                                    <i className="bi bi-play"></i>
                                  </button>
                                )}
                                {item.status === 'cutting' && (
                                  <>
                                    <button
                                      className="btn btn-outline-success"
                                      onClick={() => handleItemStatusChange(item._id, 'completed')}
                                      title="Voltooid"
                                    >
                                      <i className="bi bi-check"></i>
                                    </button>
                                    <button
                                      className="btn btn-outline-danger"
                                      onClick={() => handleItemStatusChange(item._id, 'error')}
                                      title="Fout"
                                    >
                                      <i className="bi bi-x"></i>
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                            {isEditable && (
                              <div className="dropdown d-inline-block" style={{ position: 'relative' }}>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => setShowItemDropdown(showItemDropdown === item._id ? null : item._id)}
                                  title="Verplaats naar andere machine"
                                >
                                  <i className="bi bi-arrow-right"></i>
                                </button>
                                {showItemDropdown === item._id && (
                                  <ul className="dropdown-menu dropdown-menu-end show" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 1000 }}>
                                    <li><span className="dropdown-header">Verplaats naar</span></li>
                                    {otherMachines.map(machine => (
                                      <li key={machine._id}>
                                        <button
                                          className="dropdown-item"
                                          onClick={() => {
                                            handleMoveItem(item._id, machine._id);
                                            setShowItemDropdown(null);
                                          }}
                                        >
                                          {machine.name}
                                        </button>
                                      </li>
                                    ))}
                                    {otherMachines.length === 0 && (
                                      <li><span className="dropdown-item text-muted">Geen andere machines</span></li>
                                    )}
                                  </ul>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  Geen items in deze snijlijst
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          {/* Status Card */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">Status</span>
                <span className={`badge bg-${
                  cutList.status === 'completed' ? 'success' :
                  cutList.status === 'in_progress' ? 'warning' :
                  cutList.status === 'ready' ? 'info' : 'secondary'
                }`}>
                  {cutList.status === 'completed' ? 'Voltooid' :
                   cutList.status === 'in_progress' ? 'In productie' :
                   cutList.status === 'ready' ? 'Gereed' :
                   cutList.status === 'draft' ? 'Concept' : cutList.status}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">Materiaal</span>
                <span className="badge bg-primary">{cutList.material}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Rolbreedte</span>
                <span>{cutList.rollWidth || 140} cm</span>
              </div>
            </div>
          </div>

          {/* Stock Locations Card */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">Locatie</h6>
            </div>
            <div className="card-body">
              {cutList.stockLocations?.length > 0 ? (
                <ul className="list-unstyled mb-0">
                  {cutList.stockLocations.map((loc) => (
                    <li key={loc.idlocation || loc.name} className="d-flex justify-content-between">
                      <span className="fw-medium">{loc.name}</span>
                      {loc.stock !== undefined && <span className="text-muted small">{loc.stock} op voorraad</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted mb-0">Geen locatie bekend</p>
              )}
            </div>
          </div>

          {/* Machine Card */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">Machine</h6>
            </div>
            <div className="card-body">
              {cutList.status === 'draft' || cutList.status === 'ready' ? (
                <>
                  <label className="form-label text-muted small mb-1">Wijzig machine</label>
                  <select
                    className="form-select"
                    value={cutList.machine?._id || ''}
                    onChange={(e) => handleMachineChange(e.target.value)}
                  >
                    <option value="">Selecteer machine</option>
                    {machines.filter(m => m.isActive).map((machine) => (
                      <option key={machine._id} value={machine._id}>
                        {machine.name} ({machine.type})
                      </option>
                    ))}
                  </select>
                  {cutList.machine && (
                    <small className="text-muted d-block mt-2">
                      <Link to={`/machines/${cutList.machine._id}`}>
                        <i className="bi bi-box-arrow-up-right me-1"></i>
                        Bekijk machine
                      </Link>
                    </small>
                  )}
                </>
              ) : cutList.machine ? (
                <>
                  <p className="fw-medium mb-1">
                    <Link to={`/machines/${cutList.machine._id}`}>
                      {cutList.machine.name}
                    </Link>
                  </p>
                  <p className="text-muted mb-0">{cutList.machine.type}</p>
                  <small className="text-muted">
                    <i className="bi bi-lock me-1"></i>
                    Machine kan niet meer gewijzigd worden
                  </small>
                </>
              ) : (
                <p className="text-muted mb-0">Geen machine toegewezen</p>
              )}
            </div>
          </div>

          {/* Timing Card */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">Timing</h6>
            </div>
            <div className="card-body">
              <div className="mb-2">
                <span className="text-muted d-block small">Aangemaakt</span>
                <span>{new Date(cutList.createdAt).toLocaleString('nl-NL')}</span>
              </div>
              {cutList.startedAt && (
                <div className="mb-2">
                  <span className="text-muted d-block small">Gestart</span>
                  <span>{new Date(cutList.startedAt).toLocaleString('nl-NL')}</span>
                </div>
              )}
              {cutList.completedAt && (
                <div className="mb-2">
                  <span className="text-muted d-block small">Voltooid</span>
                  <span>{new Date(cutList.completedAt).toLocaleString('nl-NL')}</span>
                </div>
              )}
              {cutList.actualDuration && (
                <div>
                  <span className="text-muted d-block small">Doorlooptijd</span>
                  <span>{Math.round(cutList.actualDuration)} minuten</span>
                </div>
              )}
            </div>
          </div>

          {/* Statistics */}
          {cutList.stats && (
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">Statistieken</h6>
              </div>
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-4">
                    <h4 className="text-primary">{cutList.stats.totalItems || 0}</h4>
                    <small className="text-muted">Totaal</small>
                  </div>
                  <div className="col-4">
                    <h4 className="text-success">{cutList.stats.completedItems || 0}</h4>
                    <small className="text-muted">Voltooid</small>
                  </div>
                  <div className="col-4">
                    <h4 className="text-danger">{cutList.stats.errorItems || 0}</h4>
                    <small className="text-muted">Fouten</small>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product Detail Modal */}
      {showProductDetailModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowProductDetailModal(false)}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {loadingProduct ? 'Product laden...' : productDetail ? productDetail.name : 'Product niet gevonden'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowProductDetailModal(false)}></button>
              </div>
              <div className="modal-body">
                {loadingProduct ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : productDetail ? (
                  <div className="row">
                    <div className="col-md-7">
                      <div className="mb-3">
                        <code className="fs-6">{productDetail.productCode}</code>
                      </div>
                      <div className="row mb-3">
                        <div className="col-6">
                          <small className="text-muted d-block">Materiaal</small>
                          <span className="badge bg-primary">{productDetail.materialType || '-'}</span>
                        </div>
                        <div className="col-6">
                          <small className="text-muted d-block">Rolbreedte</small>
                          <span className="fw-medium">{productDetail.rollWidth ? `${productDetail.rollWidth} cm` : '-'}</span>
                        </div>
                      </div>
                      {productDetail.description && (
                        <div className="mb-3">
                          <small className="text-muted d-block">Beschrijving</small>
                          <p className="mb-0">{productDetail.description}</p>
                        </div>
                      )}
                      {productDetail.parts?.length > 0 && (
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">Onderdelen ({productDetail.parts.length})</small>
                          <table className="table table-sm mb-0">
                            <thead><tr><th>Code</th><th>Naam</th><th>Aantal</th></tr></thead>
                            <tbody>
                              {productDetail.parts.map((part, i) => (
                                <tr key={i}>
                                  <td><code>{part.productcode}</code></td>
                                  <td>{part.name}</td>
                                  <td>{part.amount || 1}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {productDetail.metadata?.picqerData?.productfields?.length > 0 && (
                        <div>
                          <small className="text-muted d-block mb-1">Picqer velden</small>
                          <table className="table table-sm mb-0">
                            <tbody>
                              {productDetail.metadata.picqerData.productfields
                                .filter(f => f.value)
                                .map((field, i) => (
                                  <tr key={i}>
                                    <td className="text-muted">{field.title}</td>
                                    <td className="fw-medium">{field.value}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                    <div className="col-md-5">
                      <div className="card mb-3">
                        <div className="card-header py-2"><h6 className="mb-0">Voorraad</h6></div>
                        <div className="card-body py-2">
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Beschikbaar</span>
                            <span className="fw-medium">{productDetail.stock?.quantity || 0}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Gereserveerd</span>
                            <span className="fw-medium">{productDetail.stock?.reserved || 0}</span>
                          </div>
                        </div>
                      </div>
                      {productDetail.stockLocations?.length > 0 && (
                        <div className="card mb-3">
                          <div className="card-header py-2"><h6 className="mb-0">Locaties</h6></div>
                          <div className="card-body p-0">
                            <table className="table table-sm mb-0">
                              <tbody>
                                {productDetail.stockLocations.map((loc, i) => (
                                  <tr key={i}>
                                    <td>
                                      {loc.name}
                                      {loc.isBulkLocation && <span className="badge bg-info ms-1" style={{ fontSize: '0.6em' }}>Bulk</span>}
                                    </td>
                                    <td className="text-end">{loc.freeStock}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      <div className="card">
                        <div className="card-header py-2"><h6 className="mb-0">Machines</h6></div>
                        <div className="card-body py-2">
                          {productDetail.primaryMachine && (
                            <div className="mb-2">
                              <small className="text-muted d-block">Primair</small>
                              <span className="badge bg-primary">{productDetail.primaryMachine.name || productDetail.primaryMachine}</span>
                            </div>
                          )}
                          {productDetail.compatibleMachines?.length > 0 && (
                            <div>
                              <small className="text-muted d-block">Compatibel</small>
                              <div className="d-flex flex-wrap gap-1">
                                {productDetail.compatibleMachines.map((m, i) => (
                                  <span key={i} className="badge bg-secondary">{m.name || m}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {!productDetail.primaryMachine && !productDetail.compatibleMachines?.length && (
                            <span className="text-muted">Geen machines</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">Product niet gevonden in de database</div>
                )}
              </div>
              <div className="modal-footer">
                {productDetail && (
                  <Link to={`/products/${productDetail._id}`} className="btn btn-outline-primary me-auto">
                    <i className="bi bi-box-arrow-up-right me-1"></i>
                    Volledige pagina
                  </Link>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setShowProductDetailModal(false)}>Sluiten</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CutListDetail;
